import { Line } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useEffect, useMemo, useRef } from 'react'
import { CatmullRomCurve3, Group, Mesh, TubeGeometry, Vector3 } from 'three'

type Ponto = readonly [number, number, number]
const nenhumRaio = () => null
const eixo = new Vector3(0, 1, 0)

/** O cabo se desenrola e o conector percorre o trajeto até o acoplamento. */
export function Cabo3D({ de, para, cor, espessura = 3, barrigaM, fluxo = false, reverso = false, pausado = false, grampo = false }: {
  de: Ponto; para: Ponto; cor: string; espessura?: number; barrigaM?: number
  fluxo?: boolean; reverso?: boolean; pausado?: boolean; grampo?: boolean
}) {
  const curva = useMemo(() => {
    const comprimento = Math.hypot(para[0] - de[0], para[1] - de[1], para[2] - de[2])
    const barriga = barrigaM ?? Math.min(1.2, comprimento * 0.12)
    return new CatmullRomCurve3(Array.from({ length: 25 }, (_, i) => {
      const t = i / 24
      return new Vector3(de[0] + (para[0] - de[0]) * t, Math.max(0.08, de[1] + (para[1] - de[1]) * t - 4 * barriga * t * (1 - t)), de[2] + (para[2] - de[2]) * t)
    }))
    // Dependências numéricas: renderizar o painel não reinicia a conexão.
  }, [de[0], de[1], de[2], para[0], para[1], para[2], barrigaM])
  const geometria = useMemo(() => new TubeGeometry(curva, 96, Math.max(0.06, espessura * 0.018), 8, false), [curva, espessura])
  const conector = useRef<Group>(null)
  const particulas = useRef<(Mesh | null)[]>([])
  const tempo = useRef(pausado ? 2.2 : 0)
  useEffect(() => {
    tempo.current = pausado ? 2.2 : 0
    geometria.setDrawRange(0, pausado ? 96 * 8 * 6 : 0)
    return () => geometria.dispose()
  }, [geometria])
  useFrame((_, delta) => {
    if (!pausado) tempo.current += delta
    const progresso = Math.min(1, tempo.current / 2.2)
    geometria.setDrawRange(0, Math.floor(progresso * 96) * 8 * 6)
    if (conector.current) {
      conector.current.position.copy(curva.getPointAt(progresso))
      conector.current.quaternion.setFromUnitVectors(eixo, curva.getTangentAt(progresso).normalize())
    }
    particulas.current.forEach((mesh, i) => {
      if (!mesh) return
      mesh.visible = fluxo && progresso === 1
      const t = (tempo.current * 0.22 + i / 5) % 1
      mesh.position.copy(curva.getPointAt(reverso ? 1 - t : t))
    })
  })
  return <group>
    <mesh geometry={geometria} raycast={nenhumRaio}><meshStandardMaterial color={cor} emissive={cor} emissiveIntensity={0.22} roughness={0.65} /></mesh>
    <group ref={conector} position={[...de]}>
      {grampo ? <>
        <mesh position={[-0.07, 0.06, 0]} rotation={[0, 0, -0.18]}><boxGeometry args={[0.07, 0.35, 0.12]} /><meshStandardMaterial color="#dce4ec" metalness={0.85} roughness={0.25} /></mesh>
        <mesh position={[0.07, 0.06, 0]} rotation={[0, 0, 0.18]}><boxGeometry args={[0.07, 0.35, 0.12]} /><meshStandardMaterial color="#dce4ec" metalness={0.85} roughness={0.25} /></mesh>
      </> : <mesh><cylinderGeometry args={[0.12, 0.12, 0.28, 12]} /><meshStandardMaterial color="#dce4ec" metalness={0.85} roughness={0.25} /></mesh>}
      <mesh position={[0, -0.17, 0]}><cylinderGeometry args={[0.15, 0.15, 0.08, 12]} /><meshStandardMaterial color={cor} /></mesh>
    </group>
    {Array.from({ length: 5 }, (_, i) => <mesh key={i} ref={(mesh) => { particulas.current[i] = mesh }} visible={false} raycast={nenhumRaio}>
      <sphereGeometry args={[espessura * 0.027, 12, 8]} /><meshBasicMaterial color="#ffffff" />
    </mesh>)}
  </group>
}

/** A haste desce até o solo antes de o cabo terminar seu percurso. */
export function HasteAterramento3D({ posicao, pausado = false }: { posicao: readonly [number, number]; pausado?: boolean }) {
  const haste = useRef<Group>(null)
  const tempo = useRef(pausado ? 0.8 : 0)
  useFrame((_, delta) => {
    if (!pausado) tempo.current += delta
    if (haste.current) haste.current.position.y = 0.8 * (1 - Math.min(1, tempo.current / 0.8))
  })
  return <group position={[posicao[0], 0, posicao[1]]}>
    <group ref={haste} position={[0, 0.8, 0]}>
      <mesh position={[0, 0.3, 0]} castShadow><cylinderGeometry args={[0.045, 0.045, 0.6, 12]} /><meshStandardMaterial color="#b87333" metalness={0.8} roughness={0.35} /></mesh>
      <mesh position={[0, 0.58, 0]}><boxGeometry args={[0.2, 0.12, 0.12]} /><meshStandardMaterial color="#8fcf3a" /></mesh>
    </group>
  </group>
}

/**
 * Faísca por falta de aterramento/equipotencialização — reage ao erro que
 * `avaliarProcedimento` já detecta, não decide nada sozinha. Pisca por um
 * instante e some.
 */
export function Faisca3D({ posicao }: { posicao: readonly [number, number, number] }) {
  const grupo = useRef<Group>(null)
  const tempo = useRef(0)
  const segmentos = useMemo(() => {
    let atual = new Vector3(0, 0.4, 0)
    const pontos = [atual.clone()]
    for (let i = 0; i < 5; i++) {
      atual = atual.clone().add(new Vector3((Math.random() - 0.5) * 0.35, (Math.random() - 0.5) * 0.25, (Math.random() - 0.5) * 0.35))
      pontos.push(atual.clone())
    }
    return pontos
  }, [])
  useFrame((_, delta) => {
    tempo.current += delta
    if (grupo.current) grupo.current.visible = Math.floor(tempo.current * 24) % 2 === 0
  })
  return (
    <group ref={grupo} position={[...posicao]}>
      <Line points={segmentos} color="#bfe8ff" lineWidth={3} raycast={() => null} />
      <pointLight color="#bfe8ff" intensity={3} distance={2.5} decay={1} />
    </group>
  )
}

/** Marcador de fase sobre uma abertura. */
export function MarcadorFase3D({ posicao, fase, obstruida }: { posicao: Ponto; fase: 'liquido' | 'vapor'; obstruida: boolean }) {
  const cor = obstruida ? '#8a939c' : fase === 'liquido' ? '#3b82f6' : '#facc15'
  return <mesh position={posicao as [number, number, number]} raycast={nenhumRaio}>
    <sphereGeometry args={[0.16, 16, 12]} /><meshBasicMaterial color={cor} transparent opacity={0.9} depthTest={false} />
  </mesh>
}
